import { isUnshippedCryptoProfile, type PublicMessage, type SerializedWrappedKey } from '@adieuu/shared';
import {
  decryptMessage,
  hasHybridMessageSignature,
  verifyConversationMessageSignature,
  type MessageSignatureCheck,
} from './conversationCryptoService';
import { decryptSystemEventForDisplay } from './conversationMetadataService';
import { isSignatureCheckingActive } from '../components/messaging/messageSignatureTrust';
import {
  loadPinnedMemberIdentityIds,
  wrapsViolatePinnedMemberSet,
} from './wrappedKeyMembership';

export interface DisplayMessageLike extends PublicMessage {
  decryptedContent?: string;
  signatureVerified?: boolean;
  signatureVersion?: 1 | 2;
  /** Hybrid signature failed only because the sender's ML-DSA key was not cached yet. */
  signaturePendingPqKey?: boolean;
  /** Epoch ms until which a pending row renders as "checking" instead of unverified. */
  signatureCheckingUntil?: number;
  decryptionError?: string;
  forwardSecrecy?: boolean;
}

/** Longest a pending row may render as "checking" before falling back to the unverified banner. */
export const SIGNATURE_CHECK_DEADLINE_MS = 10_000;

/** Longest a decrypt batch waits for ML-DSA key refetches before rendering. */
export const PQ_REFETCH_WAIT_MS = 3_000;

export interface HealedSignature {
  message: DisplayMessageLike;
  /** The row had been rendered with the unverified banner (not the checking state). */
  wasVisiblyUnverified: boolean;
}

export { isSignatureCheckingActive };

function pendingSignatureFields(
  check: MessageSignatureCheck,
  checkingUntil: number | undefined
): Pick<DisplayMessageLike, 'signaturePendingPqKey' | 'signatureCheckingUntil'> {
  if (!check.pendingPqKey) return {};
  return checkingUntil != null
    ? { signaturePendingPqKey: true, signatureCheckingUntil: checkingUntil }
    : { signaturePendingPqKey: true };
}

/**
 * Signature-only recheck of a row that failed because the sender's ML-DSA key
 * was missing. Returns null when the row is not pending or the key is still
 * unavailable. Never touches the decrypted body or any key storage.
 */
export function recheckPendingSignature(
  row: DisplayMessageLike,
  senderSigningKey: string | undefined,
  senderPqSigningKey: string | undefined,
  pinnedIdentityIds: readonly string[] | null,
  identityId: string
): { row: DisplayMessageLike; healed: boolean } | null {
  if (row.signaturePendingPqKey !== true) return null;
  if (!senderSigningKey || !senderPqSigningKey) return null;

  let check: MessageSignatureCheck;
  try {
    check = verifyConversationMessageSignature(row, senderSigningKey, senderPqSigningKey);
  } catch {
    check = { verified: false, pendingPqKey: false };
  }

  const { signaturePendingPqKey: _pending, signatureCheckingUntil: _until, ...rest } = row;
  if (!check.verified) {
    return { row: { ...rest, signatureVerified: false }, healed: false };
  }
  const next = withPinnedMemberWrapPolicy(
    { ...rest, signatureVerified: true, signatureVersion: check.signatureVersion },
    row.wrappedKeys,
    pinnedIdentityIds,
    identityId
  );
  return { row: next, healed: next.signatureVerified === true };
}

export interface DecryptMessageBatchParams {
  messages: PublicMessage[];
  conversationId: string;
  /**
   * When false, skips session-key persistence, OTPK deletion, and session cache writes (e.g. read-only
   * decrypts such as E2E edit history previews).
   */
  persistSideEffects?: boolean;
  /** Set when paging (older or newer); skips reusing prior decrypt shortcuts from initial load. */
  pagingCursor?: string;
  identityId: string;
  wrappingKey: Uint8Array | null;
  ecdhPrivateKey: Uint8Array | null;
  kemPrivateKey: Uint8Array | null;
  myRoutingTag?: string;
  signingKeyCache: Record<string, string>;
  /** ML-DSA-65 verifying keys; missing/empty means classical-only history. */
  pqSigningKeyCache?: Record<string, string>;
  existingMessages: DisplayMessageLike[];
  sessionKeyCache: Map<string, Uint8Array>;
  conversationMetadataKey?: Uint8Array;
  getConversationMetadataKey?: (conversationId: string) => Uint8Array | undefined;
  fetchSigningKey: (identityId: string) => Promise<string | null>;
  resolveParticipants: (identityIds: string[]) => void;
  findAndDecryptSignedPreKey: (
    signedPreKeyId: string,
    identityId: string,
    wrappingKey: Uint8Array
  ) => Promise<{ ecdhPrivateKey: Uint8Array; kemPrivateKey: Uint8Array } | null>;
  findAndDecryptOneTimePreKey: (
    oneTimePreKeyId: string,
    identityId: string,
    wrappingKey: Uint8Array
  ) => Promise<{ ecdhPrivateKey: Uint8Array; kemPrivateKey: Uint8Array } | null>;
  deleteOneTimePreKey: (oneTimePreKeyId: string, identityId: string) => Promise<void>;
  getPersistedSessionKey: (
    messageId: string,
    identityId: string,
    wrappingKey: Uint8Array
  ) => Promise<Uint8Array | null>;
  storeSessionKey: (
    messageId: string,
    identityId: string,
    sessionKey: Uint8Array,
    wrappingKey: Uint8Array,
    signedPreKeyId?: string
  ) => Promise<void>;
  deletePersistedSessionKey: (messageId: string, identityId: string) => Promise<void>;
  notifyOtpkConsumed: () => void;
  /**
   * Locally pinned member identities. Omit to load from IndexedDB.
   * Pass `null` to skip the check (tests / no pin yet).
   */
  pinnedIdentityIds?: readonly string[] | null;
  /**
   * Fill an empty ML-DSA cache entry for a sender (throttled by the caller).
   * Called when a hybrid message would otherwise be checked without that key.
   */
  refetchPqSigningKey?: (identityId: string) => Promise<void>;
  /** Override for {@link PQ_REFETCH_WAIT_MS} (tests). */
  pqRefetchWaitMs?: number;
  /**
   * Refetches still running after the wait cap. Rows from those senders are
   * marked as checking; the caller rechecks once `settled` resolves.
   */
  onPqRefetchDeferred?: (conversationId: string, senderIds: string[], settled: Promise<void>) => void;
  /** Prior rows that verified after a signature-only recheck in this batch. */
  onSignaturesHealed?: (conversationId: string, healed: HealedSignature[]) => void;
}

/** Optional signature-refresh hooks a caller can spread into {@link DecryptMessageBatchParams}. */
export type SignatureRefreshHooks = Pick<
  DecryptMessageBatchParams,
  'refetchPqSigningKey' | 'onPqRefetchDeferred' | 'onSignaturesHealed'
>;

function withPinnedMemberWrapPolicy(
  display: DisplayMessageLike,
  wrappedKeys: SerializedWrappedKey[] | undefined,
  pinnedIdentityIds: readonly string[] | null,
  identityId: string,
): DisplayMessageLike {
  if (!wrapsViolatePinnedMemberSet(wrappedKeys, pinnedIdentityIds, [identityId])) {
    return display;
  }
  return { ...display, signatureVerified: false };
}

/** The thread summary is server-owned and changes without a new ciphertext. */
function withServerThreadSummary(prior: DisplayMessageLike, fresh: PublicMessage): DisplayMessageLike {
  if (prior.thread === fresh.thread || JSON.stringify(prior.thread) === JSON.stringify(fresh.thread)) return prior;
  return { ...prior, thread: fresh.thread };
}

export async function decryptMessageBatch(params: DecryptMessageBatchParams): Promise<DisplayMessageLike[]> {
  const {
    messages,
    conversationId,
    persistSideEffects = true,
    pagingCursor,
    identityId,
    wrappingKey,
    ecdhPrivateKey,
    kemPrivateKey,
    myRoutingTag,
    signingKeyCache,
    existingMessages,
    sessionKeyCache,
    conversationMetadataKey,
    getConversationMetadataKey,
    fetchSigningKey,
    resolveParticipants,
    findAndDecryptSignedPreKey,
    findAndDecryptOneTimePreKey,
    deleteOneTimePreKey,
    getPersistedSessionKey,
    storeSessionKey,
    deletePersistedSessionKey,
    notifyOtpkConsumed,
    pinnedIdentityIds: pinnedIdentityIdsOverride,
    pqSigningKeyCache,
    refetchPqSigningKey,
    pqRefetchWaitMs = PQ_REFETCH_WAIT_MS,
    onPqRefetchDeferred,
    onSignaturesHealed,
  } = params;

  const senderIds = [...new Set(messages.map((m) => m.fromIdentityId))];
  const missingSenderKeys = senderIds.filter((id) => !signingKeyCache[id]);
  if (missingSenderKeys.length > 0) {
    await Promise.all(
      missingSenderKeys.map(async (sid) => {
        const key = await fetchSigningKey(sid);
        if (key) signingKeyCache[sid] = key;
      })
    );
  }
  resolveParticipants(senderIds);

  const spkCache = new Map<string, { ecdh: Uint8Array; kem: Uint8Array }>();
  const otpkCache = new Map<string, { ecdh: Uint8Array; kem: Uint8Array }>();
  const deletedOtpkIds = new Set<string>();

  const metadataKey =
    conversationMetadataKey ?? getConversationMetadataKey?.(conversationId);

  const pinnedIdentityIds =
    pinnedIdentityIdsOverride !== undefined
      ? pinnedIdentityIdsOverride
      : await loadPinnedMemberIdentityIds(conversationId);

  const existingById = new Map<string, DisplayMessageLike>();
  if (!pagingCursor) {
    for (const em of existingMessages) {
      if (em.decryptedContent !== undefined) existingById.set(em.id, em);
    }
  }

  const pqDeferredSenders = await refetchMissingPqKeys({
    messages,
    existingById,
    freshlyFetchedSenders: new Set(missingSenderKeys),
    pqSigningKeyCache,
    refetchPqSigningKey,
    waitMs: pqRefetchWaitMs,
    onDeferred: onPqRefetchDeferred
      ? (senderIds, settled) => onPqRefetchDeferred(conversationId, senderIds, settled)
      : undefined,
  });
  const checkingUntil =
    pqDeferredSenders.size > 0 ? Date.now() + SIGNATURE_CHECK_DEADLINE_MS : undefined;
  const checkingUntilFor = (senderId: string) =>
    pqDeferredSenders.has(senderId) ? checkingUntil : undefined;
  const healed: HealedSignature[] = [];

  const out = await Promise.all(
    messages.map(async (m): Promise<DisplayMessageLike> => {
      if (m.deleted) {
        sessionKeyCache.delete(m.id);
        deletePersistedSessionKey(m.id, identityId).catch(() => undefined);
        return { ...m, decryptedContent: undefined, signatureVerified: undefined };
      }

      if (m.messageType === 'system') {
        return {
          ...m,
          systemEvent: decryptSystemEventForDisplay(m, conversationId, metadataKey) ?? m.systemEvent,
          decryptedContent: undefined,
          signatureVerified: undefined,
        };
      }

      if (isUnshippedCryptoProfile(m.cryptoProfile)) {
        return {
          ...m,
          decryptedContent: undefined,
          decryptionError: 'Unsupported crypto profile',
          signatureVerified: false,
        };
      }

      const cachedRow = existingById.get(m.id);
      if (cachedRow && cachedRow.ciphertext !== m.ciphertext) {
        sessionKeyCache.delete(m.id);
        void deletePersistedSessionKey(m.id, identityId).catch(() => undefined);
      }
      const prior =
        cachedRow && cachedRow.ciphertext === m.ciphertext ? withServerThreadSummary(cachedRow, m) : undefined;
      if (prior) {
        if (prior.signaturePendingPqKey === true) {
          const rechecked = recheckPendingSignature(
            prior,
            signingKeyCache[m.fromIdentityId],
            pqSigningKeyCache?.[m.fromIdentityId] || undefined,
            pinnedIdentityIds,
            identityId
          );
          if (rechecked) {
            if (rechecked.healed) {
              healed.push({
                message: rechecked.row,
                wasVisiblyUnverified: !isSignatureCheckingActive(prior),
              });
            }
            return rechecked.row;
          }
          const until = checkingUntilFor(m.fromIdentityId);
          if (until != null) {
            return withPinnedMemberWrapPolicy(
              { ...prior, signatureCheckingUntil: until },
              m.wrappedKeys,
              pinnedIdentityIds,
              identityId
            );
          }
        }
        return withPinnedMemberWrapPolicy(prior, m.wrappedKeys, pinnedIdentityIds, identityId);
      }

      let cached = sessionKeyCache.get(m.id);
      if (!cached && wrappingKey) {
        const persisted = await getPersistedSessionKey(m.id, identityId, wrappingKey);
        if (persisted) {
          cached = persisted;
          sessionKeyCache.set(m.id, persisted);
        }
      }
      if (cached && ecdhPrivateKey && kemPrivateKey) {
        const senderSigningKey = signingKeyCache[m.fromIdentityId];
        if (senderSigningKey) {
          try {
            const result = decryptMessage(
              m,
              identityId,
              ecdhPrivateKey,
              kemPrivateKey,
              senderSigningKey,
              undefined,
              undefined,
              cached,
              pqSigningKeyCache?.[m.fromIdentityId] || undefined
            );
            const hasFs = m.wrappedKeys?.some(
              (wk) => wk.identityId === identityId && wk.preKeyType !== 'static'
            ) ?? false;
            return withPinnedMemberWrapPolicy(
              {
                ...m,
                decryptedContent: result.plaintext,
                signatureVerified: result.verified,
                signatureVersion: result.signatureVersion,
                ...pendingSignatureFields(result, checkingUntilFor(m.fromIdentityId)),
                forwardSecrecy: hasFs,
              },
              m.wrappedKeys,
              pinnedIdentityIds,
              identityId
            );
          } catch {
            sessionKeyCache.delete(m.id);
          }
        }
      }

      if (!ecdhPrivateKey || !kemPrivateKey) return { ...m, decryptionError: 'Device keys unavailable' };
      const senderSigningKey = signingKeyCache[m.fromIdentityId];
      if (!senderSigningKey) return { ...m, decryptionError: 'Sender signing key unavailable' };
      if (!m.wrappedKeys || m.wrappedKeys.length === 0) return { ...m, decryptionError: 'No wrapped keys on message' };

      const candidates = m.wrappedKeys.filter((wk: SerializedWrappedKey) => wk.identityId === identityId);
      if (candidates.length === 0) {
        return { ...m, decryptionError: `No wrapped key for identity ${identityId.slice(0, 8)}...` };
      }

      let resolvedWrappedKey: SerializedWrappedKey | undefined;
      let preKeyPrivateKeys:
        | {
            spkEcdhPrivate?: Uint8Array;
            spkKemPrivate?: Uint8Array;
            otpkEcdhPrivate?: Uint8Array;
            otpkKemPrivate?: Uint8Array;
          }
        | undefined;

      const fsCandidates = candidates.filter(
        (wk) => (wk.preKeyType === 'spk' || wk.preKeyType === 'otpk') && wk.signedPreKeyId
      );
      let fsSpkMissing = false;
      let fsOtpkMissing = false;
      let fsLookupError = false;

      if (fsCandidates.length > 0 && wrappingKey) {
        for (const candidate of fsCandidates) {
          try {
            let spkKeys = spkCache.get(candidate.signedPreKeyId!);
            if (!spkKeys) {
              const decryptedSpk = await findAndDecryptSignedPreKey(
                candidate.signedPreKeyId!,
                identityId,
                wrappingKey
              );
              if (decryptedSpk) {
                spkKeys = { ecdh: decryptedSpk.ecdhPrivateKey, kem: decryptedSpk.kemPrivateKey };
                spkCache.set(candidate.signedPreKeyId!, spkKeys);
              }
            }

            if (spkKeys) {
              const candidatePreKeys: typeof preKeyPrivateKeys = {
                spkEcdhPrivate: spkKeys.ecdh,
                spkKemPrivate: spkKeys.kem,
              };

              if (candidate.preKeyType === 'otpk' && candidate.oneTimePreKeyId) {
                let otpkKeys = otpkCache.get(candidate.oneTimePreKeyId);
                if (!otpkKeys) {
                  const decryptedOtpk = await findAndDecryptOneTimePreKey(
                    candidate.oneTimePreKeyId,
                    identityId,
                    wrappingKey
                  );
                  if (decryptedOtpk) {
                    otpkKeys = { ecdh: decryptedOtpk.ecdhPrivateKey, kem: decryptedOtpk.kemPrivateKey };
                    otpkCache.set(candidate.oneTimePreKeyId, otpkKeys);
                  }
                }
                if (otpkKeys) {
                  candidatePreKeys.otpkEcdhPrivate = otpkKeys.ecdh;
                  candidatePreKeys.otpkKemPrivate = otpkKeys.kem;
                } else {
                  fsOtpkMissing = true;
                  continue;
                }
              }

              resolvedWrappedKey = candidate;
              preKeyPrivateKeys = candidatePreKeys;
              break;
            } else {
              fsSpkMissing = true;
            }
          } catch {
            fsLookupError = true;
          }
        }
      }

      if (!resolvedWrappedKey && myRoutingTag) {
        const staticCandidates = candidates.filter((wk) => wk.preKeyType === 'static' && wk.routingTag);
        const tagMatch = staticCandidates.find((wk) => wk.routingTag === myRoutingTag);
        if (tagMatch) resolvedWrappedKey = tagMatch;
      }

      if (!resolvedWrappedKey) {
        for (const candidate of candidates) {
          if (candidate.preKeyType !== 'static') continue;
          try {
            const result = decryptMessage(
              m,
              identityId,
              ecdhPrivateKey,
              kemPrivateKey,
              senderSigningKey,
              undefined,
              candidate,
              undefined,
              pqSigningKeyCache?.[m.fromIdentityId] || undefined
            );
            if (persistSideEffects) {
              sessionKeyCache.set(m.id, result.sessionKey);
            }
            return withPinnedMemberWrapPolicy(
              {
                ...m,
                decryptedContent: result.plaintext,
                signatureVerified: result.verified,
                signatureVersion: result.signatureVersion,
                ...pendingSignatureFields(result, checkingUntilFor(m.fromIdentityId)),
                forwardSecrecy: false,
              },
              m.wrappedKeys,
              pinnedIdentityIds,
              identityId
            );
          } catch {
            // Wrong static wrapped key for this device.
          }
        }

        let decryptionError: string;
        if (fsCandidates.length > 0) {
          const spkIds = fsCandidates.map((c) => c.signedPreKeyId?.slice(0, 8)).join(', ');
          if (fsOtpkMissing && !fsSpkMissing) {
            decryptionError = `forward-secrecy-expired:OTPK not found locally (SPK ${spkIds} present)`;
          } else if (fsSpkMissing && !fsOtpkMissing) {
            decryptionError = `SPK ${spkIds} not found locally`;
          } else if (fsSpkMissing && fsOtpkMissing) {
            decryptionError = `SPK ${spkIds} and OTPK not found locally`;
          } else if (fsLookupError) {
            decryptionError = `Pre-key lookup failed for SPK ${spkIds}`;
          } else {
            decryptionError = `FS key resolution failed (SPK ${spkIds})`;
          }
        } else {
          decryptionError = 'No matching wrapped key for this device';
        }
        return { ...m, decryptionError };
      }

      try {
        const result = decryptMessage(
          m,
          identityId,
          ecdhPrivateKey,
          kemPrivateKey,
          senderSigningKey,
          preKeyPrivateKeys,
          resolvedWrappedKey,
          undefined,
          pqSigningKeyCache?.[m.fromIdentityId] || undefined
        );

        if (persistSideEffects) {
          sessionKeyCache.set(m.id, result.sessionKey);
          if (wrappingKey && resolvedWrappedKey.preKeyType !== 'static') {
            storeSessionKey(
              m.id,
              identityId,
              result.sessionKey,
              wrappingKey,
              resolvedWrappedKey.signedPreKeyId
            ).catch(() => undefined);
          }

          if (
            resolvedWrappedKey.preKeyType === 'otpk' &&
            resolvedWrappedKey.oneTimePreKeyId &&
            !deletedOtpkIds.has(resolvedWrappedKey.oneTimePreKeyId)
          ) {
            deletedOtpkIds.add(resolvedWrappedKey.oneTimePreKeyId);
            deleteOneTimePreKey(resolvedWrappedKey.oneTimePreKeyId, identityId).catch(() => undefined);
            notifyOtpkConsumed();
          }
        }

        return withPinnedMemberWrapPolicy(
          {
            ...m,
            decryptedContent: result.plaintext,
            signatureVerified: result.verified,
            signatureVersion: result.signatureVersion,
            ...pendingSignatureFields(result, checkingUntilFor(m.fromIdentityId)),
            forwardSecrecy: resolvedWrappedKey.preKeyType !== 'static',
          },
          m.wrappedKeys,
          pinnedIdentityIds,
          identityId
        );
      } catch (err) {
        return { ...m, decryptionError: String(err) };
      }
    })
  );

  if (healed.length > 0) onSignaturesHealed?.(conversationId, healed);
  return out;
}

/**
 * Refetch ML-DSA keys for senders whose hybrid messages would otherwise be
 * verified without one. Waits at most `waitMs`; returns senders whose fetch
 * was still running at the cap.
 */
async function refetchMissingPqKeys(args: {
  messages: PublicMessage[];
  existingById: Map<string, DisplayMessageLike>;
  /** Senders whose keys this batch just fetched; a second request would return the same keys. */
  freshlyFetchedSenders: ReadonlySet<string>;
  pqSigningKeyCache: Record<string, string> | undefined;
  refetchPqSigningKey: ((identityId: string) => Promise<void>) | undefined;
  waitMs: number;
  onDeferred: ((senderIds: string[], settled: Promise<void>) => void) | undefined;
}): Promise<Set<string>> {
  const {
    messages,
    existingById,
    freshlyFetchedSenders,
    pqSigningKeyCache,
    refetchPqSigningKey,
    waitMs,
    onDeferred,
  } = args;
  const deferred = new Set<string>();
  if (!pqSigningKeyCache || !refetchPqSigningKey) return deferred;

  const senders = new Set<string>();
  for (const m of messages) {
    if (m.deleted || m.messageType === 'system' || isUnshippedCryptoProfile(m.cryptoProfile)) continue;
    if (pqSigningKeyCache[m.fromIdentityId] || freshlyFetchedSenders.has(m.fromIdentityId)) continue;
    const prior = existingById.get(m.id);
    if (prior && prior.ciphertext === m.ciphertext && prior.signaturePendingPqKey !== true) continue;
    if (!hasHybridMessageSignature(m)) continue;
    senders.add(m.fromIdentityId);
  }
  if (senders.size === 0) return deferred;

  const pending = new Set(senders);
  const settled = Promise.all(
    [...senders].map((sid) =>
      refetchPqSigningKey(sid)
        .catch(() => undefined)
        .finally(() => {
          pending.delete(sid);
        })
    )
  ).then(() => undefined);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const capped = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, waitMs);
  });
  await Promise.race([settled, capped]);
  if (timer !== undefined) clearTimeout(timer);

  for (const sid of pending) deferred.add(sid);
  if (deferred.size > 0) onDeferred?.([...deferred], settled);
  return deferred;
}
